/**
 * Действия панели форматирования над Markdown-исходником.
 *
 * Чистые функции: на входе текст и выделение, на выходе новый текст
 * и новое выделение. DOM здесь не появляется — иначе поведение
 * «жирный снимается повторным нажатием» нельзя было бы проверить
 * тестом без textarea.
 *
 * Набор действий ограничен тем, что умеет наш парсер (parse.js):
 * кнопка не должна рождать синтаксис, который читатель не увидит.
 * Поэтому здесь нет подчёркивания и чекбоксов — parse.js их
 * не поддерживает.
 */

/** Строчные обёртки: маркер с двух сторон выделения. */
const WRAPS = {
  bold: '**',
  italic: '_',
  strike: '~~',
  code: '`',
}

/** Найти границы строк, затронутых выделением. */
function lineSpan(text, start, end) {
  const from = text.lastIndexOf('\n', start - 1) + 1
  let to = text.indexOf('\n', end)
  if (to === -1) to = text.length
  return { from, to }
}

/** Обернуть выделение маркером или снять его повторным нажатием. */
export function toggleWrap(text, start, end, marker) {
  const before = text.slice(0, start)
  const sel = text.slice(start, end)
  const after = text.slice(end)
  const m = marker.length

  // Маркер уже внутри выделения: **жирный** выделен целиком.
  if (sel.startsWith(marker) && sel.endsWith(marker) && sel.length >= 2 * m) {
    const inner = sel.slice(m, sel.length - m)
    return { text: before + inner + after, start, end: start + inner.length }
  }
  // Маркер снаружи выделения: выделено только слово внутри **…**.
  if (before.endsWith(marker) && after.startsWith(marker)) {
    return {
      text: before.slice(0, -m) + sel + after.slice(m),
      start: start - m,
      end: end - m,
    }
  }
  // Обернуть. Пустое выделение — курсор встаёт между маркерами.
  return {
    text: before + marker + sel + marker + after,
    start: start + m,
    end: end + m,
  }
}

/**
 * Стиль блока: p | h1 | h2 | h3 | h4.
 * Меняет префикс каждой затронутой строки; p снимает заголовок.
 */
export function setBlock(text, start, end, style) {
  const { from, to } = lineSpan(text, start, end)
  const lines = text.slice(from, to).split('\n')
  const prefix = style === 'p' ? '' : '#'.repeat(Number(style.slice(1))) + ' '

  const out = lines
    .map((l) => prefix + l.replace(/^#{1,6}\s+/, ''))
    .join('\n')

  return {
    text: text.slice(0, from) + out + text.slice(to),
    start: from,
    end: from + out.length,
  }
}

/** Стиль блока в точке курсора — для подсветки в выпадающем списке. */
export function blockAt(text, pos) {
  const { from, to } = lineSpan(text, pos, pos)
  const m = text.slice(from, to).match(/^(#{1,6})\s+/)
  if (!m) return 'p'
  const level = m[1].length
  return level <= 4 ? `h${level}` : 'h4'
}

/** Общий переключатель построчного префикса (цитата, списки). */
function toggleLinePrefix(text, start, end, makePrefix, strip) {
  const { from, to } = lineSpan(text, start, end)
  const lines = text.slice(from, to).split('\n')
  const allPrefixed = lines.every((l) => strip.test(l) || !l.trim())

  const out = lines
    .map((l, i) => {
      if (!l.trim()) return l
      const bare = l.replace(strip, '')
      return allPrefixed ? bare : makePrefix(i) + bare
    })
    .join('\n')

  return {
    text: text.slice(0, from) + out + text.slice(to),
    start: from,
    end: from + out.length,
  }
}

export function toggleQuote(text, start, end) {
  return toggleLinePrefix(text, start, end, () => '> ', /^\s*>\s?/)
}

export function toggleUl(text, start, end) {
  return toggleLinePrefix(
    text, start, end,
    () => '- ',
    /^\s*(?:[-*+]|\d+[.)])\s+/
  )
}

export function toggleOl(text, start, end) {
  return toggleLinePrefix(
    text, start, end,
    (i) => `${i + 1}. `,
    /^\s*(?:[-*+]|\d+[.)])\s+/
  )
}

/**
 * Ссылка: выделенный текст становится подписью, курсор — в адресе.
 * Без выделения вставляется заготовка с выделенной подписью.
 */
export function insertLink(text, start, end) {
  const sel = text.slice(start, end) || 'текст ссылки'
  const snippet = `[${sel}](https://)`
  const out = text.slice(0, start) + snippet + text.slice(end)
  const urlStart = start + sel.length + 3 // «[» + подпись + «](»
  return { text: out, start: urlStart, end: urlStart + 'https://'.length }
}

/** Вставка блока с пустыми строками вокруг: таблица, линия. */
function insertBlock(text, start, end, block) {
  const before = text.slice(0, start)
  const after = text.slice(end)
  const pad1 = before && !before.endsWith('\n\n') ? (before.endsWith('\n') ? '\n' : '\n\n') : ''
  const pad2 = after.startsWith('\n') ? '' : '\n'
  const out = before + pad1 + block + pad2 + after
  const pos = (before + pad1 + block).length
  return { text: out, start: pos, end: pos }
}

export function insertTable(text, start, end) {
  const table = [
    '| Колонка | Колонка |',
    '| --- | --- |',
    '| Текст | Текст |',
  ].join('\n')
  return insertBlock(text, start, end, table)
}

export function insertHr(text, start, end) {
  return insertBlock(text, start, end, '---')
}

/** Чек-лист: как списки, но с квадратиком GFM. */
export function toggleTasks(text, start, end) {
  return toggleLinePrefix(
    text, start, end,
    () => '- [ ] ',
    /^\s*(?:[-*+]|\d+[.)])\s+(?:\[[ xX]\]\s+)?/
  )
}

/** Панель :::kind — выделенные строки уезжают внутрь панели. */
export function insertPanel(text, start, end, kind) {
  const { from, to } = (() => {
    const f = text.lastIndexOf('\n', start - 1) + 1
    let t = text.indexOf('\n', end)
    if (t === -1) t = text.length
    return { from: f, to: t }
  })()
  const inner = text.slice(from, to).trim() || 'Текст панели'
  const block = `:::${kind}\n${inner}\n:::`
  const r = insertBlock(text.slice(0, from) + text.slice(to), from, from, block)
  // Курсор — на текст внутри панели, чтобы сразу править.
  const innerStart = r.text.indexOf(inner, from)
  return { text: r.text, start: innerStart, end: innerStart + inner.length }
}

export function insertExpand(text, start, end) {
  const sel = text.slice(start, end).trim()
  const block = `:::expand Заголовок\n${sel || 'Скрытый текст'}\n:::`
  const r = insertBlock(text.slice(0, start) + text.slice(end), start, start, block)
  const tStart = r.text.indexOf('Заголовок', start)
  return { text: r.text, start: tStart, end: tStart + 'Заголовок'.length }
}

export function insertToc(text, start, end) {
  return insertBlock(text, start, end, '[toc]')
}

/**
 * Отступ: две пробела в начале каждой выделенной строки — ровно шаг
 * вложенности списка, который понимает парсер. Пустые строки
 * не трогаются, чтобы не плодить невидимые хвосты.
 */
export function indentLines(text, start, end) {
  const from = text.lastIndexOf('\n', start - 1) + 1
  let to = text.indexOf('\n', end)
  if (to === -1) to = text.length
  const out = text.slice(from, to).split('\n')
    .map((l) => (l.trim() ? '  ' + l : l))
    .join('\n')
  return { text: text.slice(0, from) + out + text.slice(to), start: from, end: from + out.length }
}

/** Снять отступ: до двух пробелов с начала строки, не глубже нуля. */
export function outdentLines(text, start, end) {
  const from = text.lastIndexOf('\n', start - 1) + 1
  let to = text.indexOf('\n', end)
  if (to === -1) to = text.length
  const out = text.slice(from, to).split('\n')
    .map((l) => l.replace(/^ {1,2}/, ''))
    .join('\n')
  return { text: text.slice(0, from) + out + text.slice(to), start: from, end: from + out.length }
}

const ALIGN_OPEN = /^\s*:::(left|center|right)\s*$/
const ALIGN_CLOSE = /^\s*:::\s*$/

/**
 * Выравнивание: выделенные строки оборачиваются в :::center … :::.
 * «По левому краю» — значение по умолчанию, поэтому кнопка снимает
 * контейнер, а не добавляет :::left. Повторный выбор направления
 * заменяет контейнер, а не вкладывает второй.
 */
export function setAlign(text, start, end, dir) {
  let from = text.lastIndexOf('\n', start - 1) + 1
  let to = text.indexOf('\n', end)
  if (to === -1) to = text.length
  let lines = text.slice(from, to).split('\n')

  // Ограды захвачены самим выделением.
  if (lines.length >= 2 && ALIGN_OPEN.test(lines[0]) && ALIGN_CLOSE.test(lines[lines.length - 1])) {
    lines = lines.slice(1, -1)
  } else if (from > 0 && to < text.length) {
    // Ограды стоят строками вокруг выделения.
    const prevStart = text.lastIndexOf('\n', from - 2) + 1
    const prevLine = text.slice(prevStart, from - 1)
    let nextEnd = text.indexOf('\n', to + 1)
    if (nextEnd === -1) nextEnd = text.length
    const nextLine = text.slice(to + 1, nextEnd)
    if (ALIGN_OPEN.test(prevLine) && ALIGN_CLOSE.test(nextLine)) {
      from = prevStart
      to = nextEnd
      lines = text.slice(from, to).split('\n').slice(1, -1)
    }
  }

  const inner = lines.join('\n')
  const out = dir === 'left' ? inner : `:::${dir}\n${inner}\n:::`
  return { text: text.slice(0, from) + out + text.slice(to), start: from, end: from + out.length }
}

/** Действие по имени — единая точка входа для панели и хоткеев. */
export function applyAction(action, text, start, end) {
  if (action in WRAPS) return toggleWrap(text, start, end, WRAPS[action])
  switch (action) {
    case 'quote': return toggleQuote(text, start, end)
    case 'ul':    return toggleUl(text, start, end)
    case 'ol':    return toggleOl(text, start, end)
    case 'link':  return insertLink(text, start, end)
    case 'table': return insertTable(text, start, end)
    case 'hr':    return insertHr(text, start, end)
    case 'tasks': return toggleTasks(text, start, end)
    case 'expand': return insertExpand(text, start, end)
    case 'toc':   return insertToc(text, start, end)
    case 'indent':  return indentLines(text, start, end)
    case 'outdent': return outdentLines(text, start, end)
    default:
      if (/^(p|h[1-4])$/.test(action)) return setBlock(text, start, end, action)
      if (action.startsWith('panel:')) {
        return insertPanel(text, start, end, action.slice('panel:'.length))
      }
      if (action.startsWith('align:')) {
        return setAlign(text, start, end, action.slice('align:'.length))
      }
      return { text, start, end }
  }
}

/** Хоткеи как в Confluence: Ctrl/Cmd+B, +I, +K. */
export function actionForKey(e) {
  if (!(e.ctrlKey || e.metaKey) || e.altKey) return null
  switch (e.key.toLowerCase()) {
    case 'b': return 'bold'
    case 'i': return 'italic'
    case 'k': return 'link'
    default: return null
  }
}
